import { defineCollection } from "@rebasepro/cms-types";
import type { CollectionConfig } from "@rebasepro/types";
import { personName, playerOf } from "../people";
import playersCollection from "./players";
import { SAY, moment } from "../words";

// A browser that said yes to notifications (Web Push): where its push service takes messages for it, and the keys that
// lock them so only that browser reads them. One row per browser, kept by account. backend/src/invites.ts writes it when
// a phone turns notifications on, and deletes a row once its push service says the browser is gone. The Daily Gig
// reminder (backend/crons/daily-remind.ts) reads and writes the remind fields.
const pushSubscriptionsCollection = defineCollection({
    name: "Push subscriptions",
    singularName: "Push subscription",
    slug: "push_subscriptions",
    table: "songbuzz_push",
    description: "Browsers that turned notifications on, for friends asking them to a game and the Daily Gig reminder.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
    ],
    properties: {
        // the endpoint's hash: endpoints run to hundreds of characters
        id: { name: "Id", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        who: { name: "Account", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        player: playerOf("Player", "who", "player", (): CollectionConfig => playersCollection),
        endpoint: { name: "Endpoint", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        p256dh: { name: "Key", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        auth: { name: "Secret", type: "string", validation: { required: true }, admin: { readOnly: true, hideFromCollection: true } },
        lang: { name: "Notifications in", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "language" } } },
        at: { name: "Turned on", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
        lastSent: { name: "Last sent", type: "date", admin: { readOnly: true } },
        // the hour (UTC, 0-23) the Daily Gig reminder comes at; empty: no reminder
        remindAt: { name: "Daily reminder at (UTC hour)", description: "Empty: no Daily Gig reminder", type: "number", columnType: "integer", validation: { min: 0, max: 23 } },
        remindMiss: { name: "Reminders not played", description: "Reminders in a row that weren't followed by a Daily; at 3 the reminder turns itself off", type: "number", columnType: "integer", defaultValue: 0, validation: { required: true }, admin: { readOnly: true } },
        // the Daily day a friend's "beat your score" last reached this account (one a day)
        beatDay: { name: "Last beaten on", description: "The Daily Gig day a friend's better score was last pushed here", type: "string", admin: { readOnly: true, Preview: "../../admin/src/previews/Words", customProps: { say: "day" } } },
    },
    indexes: [
        { on: ["who"], reason: "an account's browsers, each time a friend asks them to a game" },
        { on: ["remindAt"], reason: "the browsers due a Daily Gig reminder, every hour" },
    ],
    admin: {
        icon: "BellRing",
        group: "System",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: async ({ entity, context }) => personName(context, entity.values.who),
            // the browser's push service says which browser it is
            subtitle: ({ entity }) => {
                let host = "";
                try { host = new URL(String(entity.values.endpoint)).host; } catch { /* no endpoint to read */ }
                const browser = /apple/.test(host) ? "Safari (iPhone, iPad or Mac)" : /google|fcm/.test(host) ? "Chrome or Android"
                    : /mozilla/.test(host) ? "Firefox" : /windows|notify/.test(host) ? "Edge" : host;
                return [browser, entity.values.lang && `in ${SAY.language(entity.values.lang)}`, entity.values.lastSent ? `last sent ${moment(entity.values.lastSent)}` : "nothing sent yet"].filter(Boolean).join(" · ");
            },
            date: "at",
        },
        form: {
            sections: [
                { key: "push", properties: ["player", "lang", "at", "lastSent", "remindAt", "remindMiss", "beatDay"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["id", "who", "endpoint", "p256dh", "auth"] },
            ],
        },
    },
});

export default pushSubscriptionsCollection;
